import {
  Link,
  Outlet,
  createRootRoute,
  useRouter,
  useRouterState
} from '@tanstack/react-router'
import type { JSX } from 'react'
import { Toaster } from 'sonner'

import { AccessGuard, useAccess } from '../components/access-guard.tsx'
import { ThemeToggle } from '../components/theme-toggle.tsx'
import { Button } from '../design-system/button.tsx'
import { Icon } from '../design-system/icon.tsx'
import { Link as RoutedLink } from '../design-system/link.tsx'
import { Notice } from '../design-system/notice.tsx'
import { viewablePages } from '../lib/access.ts'
import { useSession } from '../session.tsx'

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: function RouterError({ error }) {
    return (
      <div className="p-8">
        <Notice tone="danger" data-testid="router-error">
          {error.message}
        </Notice>
      </div>
    )
  }
})

const RAIL_LINK =
  'hover:bg-sidebar-accent focus-visible:ring-sidebar-ring rounded-md px-2 py-1 focus-visible:ring-2 focus-visible:outline-none'

/**
 * AppNav — the rail's links: Home, every page this viewer may see, then Access.
 *
 * The middle section is server-driven. A page the backend does not return is
 * not listed, which is convenience rather than security: the guard below and
 * the page's own handler are what actually refuse.
 *
 * @returns The navigation list.
 */
function AppNav({ pathname }: { pathname: string }): JSX.Element {
  const { data } = useAccess()
  const router = useRouter()
  const pages = data ? viewablePages(data) : []

  return (
    <nav className="flex flex-row gap-2 text-sm md:flex-col">
      <Link
        data-testid="nav-home"
        to="/"
        className={RAIL_LINK}
        activeProps={{ 'aria-current': 'page' }}
      >
        {'Home'}
      </Link>
      {/*
        A registry path is a string at runtime, so it cannot be TanStack's
        literal `to` union without an assertion. The design-system Link takes
        the string and routes through history instead, which needs no cast and
        still renders a real anchor.
      */}
      {pages.map(page => (
        <RoutedLink
          key={page.id}
          data-testid={`nav-${page.id}`}
          href={page.path}
          onNavigate={() => {
            router.history.push(page.path)
          }}
          className={RAIL_LINK}
          {...(pathname === page.path ? { 'aria-current': 'page' } : {})}
        >
          {page.title}
        </RoutedLink>
      ))}
      <Link
        data-testid="nav-access"
        to="/access"
        className={RAIL_LINK}
        activeProps={{ 'aria-current': 'page' }}
      >
        {'Access'}
      </Link>
    </nav>
  )
}

/**
 * RootLayout — the signed-in shell: a left rail, and the routed surface.
 *
 * The rail is the navy inverse surface, so its controls take the sidebar's own
 * foreground and ring tokens rather than the house ones — a light-surface
 * focus ring on navy is under the 3:1 non-text floor.
 *
 * @returns The shell.
 */
function RootLayout(): JSX.Element {
  const { userName, onSignOut } = useSession()
  const pathname = useRouterState({ select: state => state.location.pathname })

  return (
    <div
      data-testid="app-root"
      className="bg-background text-foreground grid min-h-svh grid-rows-[auto_1fr] md:grid-cols-rail md:grid-rows-1"
    >
      <aside className="bg-sidebar text-sidebar-foreground flex flex-col gap-6 p-4 md:min-h-svh">
        <p data-testid="app-title" className="text-lg font-semibold">
          {'BlytzPay Internal'}
        </p>
        <AppNav pathname={pathname} />
        <div className="flex items-center gap-2 md:mt-auto">
          <p
            data-testid="session-user"
            className="min-w-0 flex-1 truncate font-mono text-xs"
          >
            {userName}
          </p>
          <ThemeToggle
            data-testid="session-theme"
            className="hover:bg-sidebar-accent focus-visible:ring-sidebar-ring text-sidebar-foreground"
          />
        </div>
        <Button
          data-testid="session-sign-out"
          variant="outline"
          size="sm"
          className="text-sidebar-foreground border-sidebar-border hover:bg-sidebar-accent focus-visible:ring-sidebar-ring"
          onClick={onSignOut}
        >
          <Icon name="log-out" />
          {'Sign out'}
        </Button>
      </aside>
      <div className="p-8">
        <AccessGuard pathname={pathname}>
          <Outlet />
        </AccessGuard>
      </div>
      <Toaster />
    </div>
  )
}
